import { Box, Typography, Button, IconButton, Modal } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { getCharacters } from '../../data/characters';
import { useState, useRef, useEffect, useMemo } from 'react'; // 添加useMemo优化
import LoginModal from '../../components/LoginModal';
import { notifyAuthStateChange } from '../../components/UserReadings';
import SplitText from './SplitText';
import { languages, useLanguage, navigateWithLang } from '../../data/languages';

function AboutContent({  }) {
  const navigate = useNavigate();
  const currentLang = useLanguage(); // 响应式语言获取
  const config = useMemo(() => languages[currentLang] || languages['en'], [currentLang]); // 优化语言配置获取
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const loginSourceRef = useRef('character');
  const [hoveredButton, setHoveredButton] = useState(null);
  const [characterButtonClicked, setCharacterButtonClicked] = useState(false);
  const [fortuneButtonClicked, setFortuneButtonClicked] = useState(false);
  const [currentDescription, setCurrentDescription] = useState(config.about.description.default);
  const [isHoverText, setIsHoverText] = useState(false);

  // Function to handle login modal
  const handleLoginModalOpen = (source) => {
    loginSourceRef.current = source;
    setLoginModalOpen(true);
  };

  const handleLoginModalClose = () => {
    setLoginModalOpen(false);
    // Proceed to the intended destination regardless of login status
    proceedToDestination(loginSourceRef.current);
  };

  // Function to proceed to the intended destination based on source
  const proceedToDestination = (source) => {
    if (source === 'fortune') {
      proceedToFortuneReading();
    } else if (source === 'character') {
      navigateWithLang(navigate, '/character');
    }
  };

  // Modified to first check if user is logged in and open modal if not
  const handle_daily_weekly_fortune = () => {
    proceedToFortuneReading();
  };

  // Function to proceed to fortune reading after successful login
  const proceedToFortuneReading = () => {
    // Select a random character from the characters array
    const allCharacters = getCharacters(currentLang);
    const randomIndex = Math.floor(Math.random() * allCharacters.length);
    const randomCharacter = allCharacters[randomIndex];

    // Navigate to the fortune page with the random character
    navigateWithLang(navigate, '/fortune', {
      state: {
        character: randomCharacter,
        isDailyFortune: true
      }
    });
  };

  const handleUserLogin = (userData) => {
    // Trigger auth state change notification to update all components
    notifyAuthStateChange();

    // After successful login, close modal and navigate based on the login source
    handleLoginModalClose();
  };

  // Handle selecting a character with login check
  const handleSelectCharacter = () => {
    navigateWithLang(navigate, '/character');
  };

  const handleScrollDown = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  // Handle character button click with animation
  const handleCharacterButtonClick = () => {
    setCharacterButtonClicked(true);
    setTimeout(() => {
      setCharacterButtonClicked(false);
      handleSelectCharacter();
    }, 300);
  };

  // Handle fortune button click with animation
  const handleFortuneButtonClick = () => {
    setFortuneButtonClicked(true);
    setTimeout(() => {
      setFortuneButtonClicked(false);
      handle_daily_weekly_fortune();
    }, 300);
  };

  useEffect(() => { // 更新描述文本
    const timer = setTimeout(() => {
      if (hoveredButton === 'character') {
        setIsHoverText(true);
        setCurrentDescription(config.about.description.character);
      } else if (hoveredButton === 'fortune') {
        setIsHoverText(true);
        setCurrentDescription(config.about.description.fortune);
      } else if (hoveredButton === null) {
        setIsHoverText(false);
        setCurrentDescription(config.about.description.default);
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [hoveredButton, config]);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Typography variant="h2" gutterBottom>
        {config.about.subtitle}
      </Typography>
      <Typography
        variant="h1"
        component="h1"
        gutterBottom
        sx={{
          fontSize: { xs: '5rem', md: '8rem' },
          letterSpacing: '0.09em',
          lineHeight: 1.1,
          position: 'relative',
          transition: 'all 0.3s ease-in-out',
          cursor: 'default',
          '&:hover': {
            textShadow: '0 0 10px rgba(0,0,0,0.2), 0 0 20px rgba(0,0,0,0.2), 0 0 30px rgba(0,0,0,0.2)',
            transform: 'scale(1.02)',
          }
        }}
      >
        {config.about.title}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            '& .clicked': {
              animation: 'popIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important'
            },
            '@keyframes popIn': {
              '0%': {
                transform: 'scale(1)',
              },
              '50%': {
                transform: 'scale(1.1)',
              },
              '100%': {
                transform: 'scale(1)',
              }
            }
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCharacterButtonClick}
            onMouseEnter={() => {
              setHoveredButton('character');
            }}
            onMouseLeave={() => {
              setHoveredButton(null);
            }}
            className={characterButtonClicked ? 'clicked' : ''}
            sx={{
              borderColor: 'primary.main',
              color: 'primary.main',
              px: 4,
              py: 1.5,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'scale(1)',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
            >
              {config.about.selectCharacter}
            </Button>
            <Button
              variant="contained"
              onClick={handleFortuneButtonClick}
              onMouseEnter={() => {
                setHoveredButton('fortune');
              }}
              onMouseLeave={() => {
                setHoveredButton(null);
              }}
              className={fortuneButtonClicked ? 'clicked' : ''}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                px: 4,
                py: 1.5,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: 'scale(1)',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              {config.about.dailyFortune}
            </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            position: 'relative',
            // top: '10vh',
            minHeight: '4rem', // Add fixed height to prevent layout shift
          }}
        >
          <Box
            sx={{
              minHeight: '5rem', // Ensure consistent height during animation
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            <SplitText
              text={currentDescription}
              className="description-text"
              delay={isHoverText ? 30 : 15} // Faster for original text
              animationFrom={isHoverText
                ? { opacity: 0, transform: 'translate3d(0,15px,0)' } // Slide up for hover text
                : { opacity: 0, transform: 'translate3d(0,-20px,0)', scale: 0.95 } // Slide down with scale for original text
              }
              animationTo={{ opacity: 1, transform: 'translate3d(0,0,0)', scale: 1 }}
              easing={isHoverText ? "easeOutQuad" : "easeOutBack"} // Different easing for different effects
              threshold={0.1}
              rootMargin="-50px"
              textAlign="center"
              key={`${currentDescription}-${isHoverText}`} // Key helps React identify when to re-render
              style={{
                fontSize: isHoverText ? '1.5rem' : '1.3rem',
                fontWeight: 500,
                lineHeight: 1.6,
                fontFamily: 'inherit',
                color: 'rgba(0, 0, 0, 0.87)' // Match MUI Typography color
              }}
            />
          </Box>

          {/* Add back the down arrow button */}
          <IconButton
            onClick={handleScrollDown}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                transform: 'translateY(2px)',
              },
              transition: 'all 0.3s ease',
              width: '40px',
              height: '40px',
              '& .MuiSvgIcon-root': {
                fontSize: '1.5rem'
              }
            }}
          >
            <KeyboardArrowDownIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Login Modal */}
      <LoginModal
        open={loginModalOpen}
        onClose={handleLoginModalClose}
        onUserLogin={handleUserLogin}
      />
    </Box>
  );
}

export default AboutContent;